<template>
  <div v-if="userProjects && !waitForApi" class="project-overview">
    <FilterManager :items="projects" :filter="filter" :filter-options="filterOptions" v-slot="filterProps">
      <QuickSearchManager :items="filterProps.items" :query="quickSearch" v-slot="quickSearchProps">
        <SortManager :items="quickSearchProps.items" :sorting="sorting" v-slot="sortProps">
          <div class="project-overview__container">
            <div class="project-overview__bar">
              <ProjectOverviewTopBar
                :items="projects"
                :filter-options="filterOptions"
                v-model:filter="filter"
                v-model:sorting="sorting"
                v-model:quick-search="quickSearch"
                @filter-overlay-open="(status) => (filterOverlayOpen = status)"
              />
            </div>

            <div v-if="sortProps.items.length" class="project-overview__list">
              <ProjectList :projects="sortProps.items" :user="user" />
            </div>

            <NothingHere v-else />
          </div>
        </SortManager>
      </QuickSearchManager>
    </FilterManager>
  </div>
  <ViewLoading delay="1.5s" data-test-id="project-overview-loader" v-else />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState } from 'vuex';
import decode from 'jwt-decode';

import ProjectList from './ProjectList.vue';
import SortManager from '@components/SortManager.vue';
import FilterManager from '@/components/GlobalFilter/FilterManager.vue';
import { setDocumentTitle } from '@common/util';
import { setNameForDeletedUsers } from '@/util/setNameForDeletedUsers';
import ProjectOverviewTopBar from './ProjectOverviewTopBar.vue';
import NothingHere from '@/components/EmptyState/NothingHere.vue';
import { flatten, map, assoc, pluck, uniqBy, pipe, prop } from 'ramda';
import { ProjectEvent } from '@/tracking/trackingTypes';
import filterOptions from './filterOptions';
import { FilterList } from '@/filter/types';
import { createFilter } from '@/filter/createFilter';
import { ProjectWithType, Project, ProjectType, ProjectTeamUser, ProjectTeamMember } from './types';
import QuickSearchManager from '@/components/QuickSearchManager.vue';
import { user as userPollInterval } from '@utils/pollIntervals';
import { acceptInviteToProject, getSuccessNotification } from '@utils/projectInvite';
import getErrorByCode from '@utils/getErrorByCode';
import {
  AcceptProjectInviteLookup,
  ProjectCreatedDocument,
  UserDocument,
  UserProjectsDocument,
  UserQuery,
  UserQueryVariables,
} from '@/graphql/operations.gen';
import ViewLoading from '@/components/ViewLoading';

const addType = (type: ProjectType, items: Array<Project>) => map(assoc('type', type), items);

const uniqByUserId = uniqBy<ProjectTeamUser, string>(prop<'id', string>('id'));

const getOwners = pipe<
  Array<Project>,
  Array<ProjectTeamUser>,
  Array<ProjectTeamUser>,
  Array<Pick<ProjectTeamUser, 'id' | 'fullname'>>
>(pluck('owner'), uniqByUserId, setNameForDeletedUsers);

const getTeamMembers = pipe<
  Array<Project>,
  Array<Array<ProjectTeamMember>>,
  Array<ProjectTeamMember>,
  Array<ProjectTeamUser>,
  Array<ProjectTeamUser>,
  Array<Pick<ProjectTeamUser, 'id' | 'fullname'>>
>(pluck('activeTeamMembers'), flatten, pluck('user'), uniqByUserId, setNameForDeletedUsers);

export default defineComponent({
  name: 'ProjectOverview',

  components: {
    FilterManager,
    SortManager,
    ProjectList,
    NothingHere,
    ProjectOverviewTopBar,
    QuickSearchManager,
    ViewLoading,
  },

  props: {
    user: {
      type: Object,
      required: true,
    },

    projectType: {
      type: String,
      required: false,
      default: 'all',
    },
  },

  data(): {
    sorting: [string, number, string];
    filter: FilterList;
    filterOverlayOpen: boolean;
    waitForApi: boolean;
    quickSearch: string;
  } {
    const filter: FilterList = { filters: [], mode: 'all' };

    if (this.projectType !== 'all') {
      const newFilter = createFilter('ProjectTypeFilter', 'item', {
        is: 'singleSelect',
      });

      newFilter.value.value = [this.projectType];
      filter.filters.push(newFilter);
    }

    return {
      sorting: ['createdAt', -1, 'string'],
      filter,
      filterOverlayOpen: false,
      quickSearch: '',
      waitForApi: false,
    };
  },

  apollo: {
    userProjects() {
      return {
        query: UserProjectsDocument,
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        pollInterval: userPollInterval,
        update({ user }) {
          return user;
        },
        result(result) {
          /**
           * Special case.
           * When inviteToken is set, loading projects and accepting invite run in parallel
           * When invite is accepted, the cache is populated even if projects haven't been loaded from api yet,
           * which means only a single project is shown.
           * Therefore, delay showing any projects until api request is finished
           */
          if (result.networkStatus === 1 && this.inviteToken) {
            this.waitForApi = true;
          } else if (result.networkStatus === 7) {
            this.waitForApi = false;
          }
        },
        error({ graphQLErrors }) {
          if (graphQLErrors) {
            graphQLErrors.forEach((error) => {
              console.error(error);
            });
          }
        },
      };
    },
    $subscribe: {
      projectCreated: {
        query: ProjectCreatedDocument,
        result({ data }) {
          const cache = this.$apollo.getClient().cache;

          cache.updateQuery(
            {
              query: UserProjectsDocument,
            },
            (cacheData) => {
              const user = cacheData?.user
                ? {
                    ...cacheData.user,
                    projects: [data.projectCreated],
                  }
                : {
                    ...cache.readQuery<UserQuery, UserQueryVariables>({
                      query: UserDocument,
                    }).user,
                    projects: [data.projectCreated],
                    projectsInvited: [],
                    projectsJoined: [],
                  };

              return {
                user,
              };
            }
          );
        },
      },
    },
  },

  computed: {
    owners(): Array<Project['owner']> {
      if (this.filterOverlayOpen || this.filter.filters.length) {
        return getOwners(this.projects);
      }

      return [];
    },

    teamMembers(): Array<Project['activeTeamMembers'][0]['user']> {
      if (this.filterOverlayOpen || this.filter.filters.length) {
        return getTeamMembers(this.projects);
      }

      return [];
    },

    filterOptions(): ReturnType<typeof filterOptions> {
      return filterOptions({
        owners: this.owners,
        teamMembers: this.teamMembers,
        types: ['owned', 'joined', 'invited'].map((type) => ({ id: type, name: this.$t(`project.filter.${type}`) })),
      });
    },

    projects(): Array<ProjectWithType> {
      return this.userProjects
        ? flatten([
            addType('owned', this.userProjects.projects),
            addType('joined', this.userProjects.projectsJoined),
            addType('invited', this.userProjects.projectsInvited),
          ])
        : [];
    },
    inviteToken: mapState('user', ['inviteToken']).inviteToken,
  },
  methods: {
    /**
     * Accept an invitation to a project from a link (via invite email)
     */
    acceptProjectInvite() {
      acceptInviteToProject({ inviteToken: this.inviteToken, lookupType: AcceptProjectInviteLookup.Token })
        .then(() => {
          const { id } = decode(this.inviteToken);

          this.$store.commit('user/USER_SET_INVITE_TOKEN', null);
          this.$store.dispatch('notification/add', getSuccessNotification(id));
        })
        .catch((error) => {
          let errorKey = null;

          if (getErrorByCode(error, 'INVITATION_PROJECT_NOT_FOUND')) {
            errorKey = 'team.invitation.projectNotFound';
          } else if (getErrorByCode(error, 'INVITATION_INVALID')) {
            errorKey = 'team.invitation.invitationInvalid';
          }

          if (errorKey) {
            this.$store.dispatch('notification/add', {
              message: this.$t(errorKey),
            });
          }
        });
    },
  },

  mounted() {
    // We don't use afterRouteUpdate here because it is called with Base as the component
    setDocumentTitle(`ATLAS.ti | ${this.$t('projectOverview.projects')}`);

    if (this.inviteToken) {
      this.acceptProjectInvite();
    }

    this.$tracker.track(ProjectEvent.ProjectOverviewViewed);
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
.project-overview {
  height: 100%;
  overflow-y: auto;
  background-color: colors.$color-grey-50;
}

.project-overview__container {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin: 0 auto;
  min-height: 100%;
}

.project-overview__list {
  padding: 0 32px 32px;
}
</style>
